// Hook de tracking GPS continuo para BoomeroPage.
//
// - Usa navigator.geolocation.watchPosition
// - Muestrea cada ~5s (controlado por el dispositivo + maximumAge)
// - Filtro de jitter: descarta puntos a menos de 2m del último aceptado
// - Buffer en memoria + flush periódico cada 30s a BD
// - Se activa cuando enabled=true (entre inicio_at e !fin_at)

import { useEffect, useRef } from 'react';
import { supabase } from '../../supabase';
import type { AppBoletaCampoTrack } from '../../types/empacadora';

interface GpsTrackerOpts {
  enabled: boolean;          // true cuando ya hay inicio_at y todavía no hay fin_at
  empresaId: number;
  boletaId: number | null;
  boomNum?: number | null;       // boom activo: 1..N completos, 0=fracción, null=sin asignar
  umbralJitterMetros?: number;   // default 2m
  intervaloFlushMs?: number;     // default 30000 (30s)
  onPuntoAceptado?: (p: AppBoletaCampoTrack) => void;
  onFlushOk?: (cantidad: number) => void;       // tras insertar N puntos con éxito
  onError?: (msg: string) => void;
}

const R = 6_371_000;
function distanciaM(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function useGpsTracker({
  enabled,
  empresaId,
  boletaId,
  boomNum,
  umbralJitterMetros = 2,
  intervaloFlushMs = 30000,
  onPuntoAceptado,
  onFlushOk,
  onError,
}: GpsTrackerOpts) {
  const watchIdRef = useRef<number | null>(null);
  const bufferRef  = useRef<AppBoletaCampoTrack[]>([]);
  const ultimoRef  = useRef<{ lat: number; lng: number } | null>(null);
  const flushTimerRef = useRef<number | null>(null);
  const boomNumRef = useRef<number | null>(null);
  // Mantener ref actualizado del boom activo para que watchPosition lo lea
  boomNumRef.current = boomNum ?? null;

  // Flush manual del buffer hacia BD.
  const flush = async () => {
    if (!bufferRef.current.length) return;
    const lote = bufferRef.current.splice(0, bufferRef.current.length);
    try {
      const { error } = await supabase.from('app_boleta_campo_track').insert(lote);
      if (error) {
        // si falla, devolver al buffer para reintentar
        bufferRef.current.unshift(...lote);
        // Si la tabla no existe o PostgREST no la ve, parar de reintentar para no spam.
        if ((error as any).code === '42P01' || /relation .* does not exist/i.test(error.message) || /not found/i.test(error.message)) {
          bufferRef.current.length = 0;
          onError?.('La tabla de recorrido GPS no está disponible. Avisar al administrador (recargar schema en Supabase).');
          return;
        }
        // RLS o permiso → mensaje específico
        if ((error as any).code === '42501' || /permission denied|row-level security/i.test(error.message)) {
          bufferRef.current.length = 0;
          onError?.(`Permiso denegado al guardar recorrido GPS (RLS): ${error.message}`);
          return;
        }
        onError?.(error.message ?? 'Error guardando recorrido GPS');
      } else {
        onFlushOk?.(lote.length);
      }
    } catch {
      bufferRef.current.unshift(...lote);
    }
  };

  useEffect(() => {
    if (!enabled || !boletaId) return;
    if (!('geolocation' in navigator)) {
      onError?.('Este dispositivo no soporta GPS.');
      return;
    }

    // Reset estado local al activar
    ultimoRef.current = null;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const ultimo = ultimoRef.current;
        if (ultimo) {
          const d = distanciaM(ultimo, { lat, lng });
          if (d < umbralJitterMetros) return; // jitter
        }
        ultimoRef.current = { lat, lng };
        const punto: AppBoletaCampoTrack = {
          empresa_id: empresaId,
          boleta_id: boletaId,
          t: new Date(pos.timestamp).toISOString(),
          lat,
          lng,
          accuracy_m: pos.coords.accuracy,
          speed_ms: pos.coords.speed ?? undefined,
          heading_deg: pos.coords.heading ?? undefined,
          boom_num: boomNumRef.current ?? undefined,
        };
        bufferRef.current.push(punto);
        onPuntoAceptado?.(punto);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) onError?.('Permiso de GPS denegado. Activá la ubicación.');
        else if (err.code === err.POSITION_UNAVAILABLE) onError?.('Sin señal GPS. El recorrido no se está registrando.');
        else if (err.code === err.TIMEOUT) onError?.('Timeout GPS — reintentando…');
        else onError?.(err.message || 'Error de GPS');
      },
      { enableHighAccuracy: true, maximumAge: 4000, timeout: 15000 },
    );

    // Primer flush rápido (10s) para verificar conectividad/permisos antes de
    // acumular muchos puntos. Luego cada `intervaloFlushMs` (default 30s).
    const primerFlush = window.setTimeout(flush, 10000);
    flushTimerRef.current = window.setInterval(flush, intervaloFlushMs);

    return () => {
      window.clearTimeout(primerFlush);
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (flushTimerRef.current != null) {
        window.clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      // flush final al desactivar
      flush();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, boletaId, empresaId]);
}
